// src/app/pages/admin/AdminEventoAsociacionOrientacionesWrapper.tsx
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import styleScss from './styles.module.scss';
import {
	getListaDireccionamientos,
	getOrientacionesAsociadasEvento,
	asociarOrientacionEvento,
	desasociarOrientacionEvento,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Alert,
	AlertTitle,
	Button,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
} from '@mui/material';
import { getErrorMessage } from './EventosEmpleabilidad.helpers';

dayjs.extend(utc);

const EVENTOS_LIST_PATH = '/admin/gestion-eventos';

const ESTADOS_DIRECCIONAMIENTO_ASOCIABLES = ['Creado', 'Registrados'];

interface OrientacionAsociacion {
	id: number;
	codigo_direccionamiento: string;
	fecha_registro: string;
	estado: string;
	tipoDireccionamiento?: {
		label: string;
	};
	subtipoDireccionamiento?: {
		label: string;
	};
}

interface AsociacionOrientacionesPageProps {
	embedded?: boolean;
	readOnly?: boolean;
}

const AsociacionOrientacionesPage = ({ embedded = false, readOnly = false }: AsociacionOrientacionesPageProps = {}) => {
	const { id: eventoId } = useParams();
	const [codigoDireccionamiento, setCodigoDireccionamiento] = useState('');
	const [orientaciones, setOrientaciones] = useState<OrientacionAsociacion[]>([]);
	const [asociadas, setAsociadas] = useState<number[]>([]);
	const [buscado, setBuscado] = useState(false);
	const [loading, setLoading] = useState(false);
	const [procesandoId, setProcesandoId] = useState<number | null>(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [open, setOpen] = useState(false);
	const [alert, setAlert] = useState(true);
	const [alertMsg, setAlertMsg] = useState('');

	const notify = (msg: string, success = true) => {
		setAlert(success);
		setAlertMsg(msg);
		setOpen(true);
		setTimeout(() => setOpen(false), 3000);
	};

	const mapAsociacionToOrientacion = (asociacion: any): OrientacionAsociacion => {
		const direccionamiento = asociacion.Direccionamiento || {};
		return {
			id: direccionamiento.id,
			codigo_direccionamiento: direccionamiento.codigo_direccionamiento,
			fecha_registro: direccionamiento.fecha_registro,
			estado: direccionamiento.estado,
			tipoDireccionamiento: direccionamiento.tipoDireccionamiento,
			subtipoDireccionamiento: direccionamiento.subtipoDireccionamiento,
		};
	};

	const cargarAsociadas = () => {
		if (!eventoId) return;
		setLoading(true);
		getOrientacionesAsociadasEvento(eventoId)
			.then((response: any) => {
				const asociaciones = response?.data || [];
				setAsociadas(asociaciones.map((asociacion: any) => asociacion.direccionamiento_id));
				setOrientaciones(asociaciones.map(mapAsociacionToOrientacion));
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		cargarAsociadas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventoId]);

	const handleBuscar = () => {
		if (!codigoDireccionamiento.trim()) {
			notify('Debe ingresar el código del direccionamiento', false);
			return;
		}
		setLoading(true);
		getListaDireccionamientos({ search: codigoDireccionamiento.trim(), limit: 50 })
			.then((response: any) => {
				const resultados = response?.data?.data || [];
				setOrientaciones(
					resultados.filter((orientacion: any) => ESTADOS_DIRECCIONAMIENTO_ASOCIABLES.includes(orientacion.estado))
				);
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
	};

	const handleKeyDownBuscar = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') handleBuscar();
	};

	const handleLimpiarFiltros = () => {
		setCodigoDireccionamiento('');
		cargarAsociadas();
	};

	const handleAsociar = (direccionamientoId: number) => {
		if (!eventoId) return;
		setProcesandoId(direccionamientoId);
		asociarOrientacionEvento(eventoId, direccionamientoId)
			.then(() => {
				setAsociadas((prev) => [...prev, direccionamientoId]);
				notify('Orientación asociada al evento con éxito.');
				handleLimpiarFiltros();
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setProcesandoId(null));
	};

	const handleDesasociar = (direccionamientoId: number) => {
		if (!eventoId) return;
		setProcesandoId(direccionamientoId);
		desasociarOrientacionEvento(eventoId, direccionamientoId)
			.then(() => {
				setAsociadas((prev) => prev.filter((asociadaId) => asociadaId !== direccionamientoId));
				notify('Asociación eliminada con éxito.');
				handleLimpiarFiltros();
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setProcesandoId(null));
	};

	return (
		<div className={embedded ? '' : 'p-4 shadow-4 rounded-3 mt-15'} style={embedded ? undefined : { backgroundColor: '#FFFF' }}>
			{!embedded && <LoadingBackdrop loading={loading} />}
			{embedded ? (
				<h3 className='mb-4'>Asociación de Orientaciones</h3>
			) : (
				<div className='row mb-4' style={{ alignItems: 'center' }}>
					<div className='col-sm-12 d-flex align-items-center' style={{ gap: '15px' }}>
						<BackButton to={EVENTOS_LIST_PATH} />
						<h2 className='m-0'>Asociación de Orientaciones</h2>
					</div>
				</div>
			)}

			<div className='input-group d-flex flex-wrap align-items-center' style={{ gap: '10px' }}>
				<div style={{ minWidth: 280 }}>
					<TextField
						fullWidth
						size='small'
						label='Filtro por código direccionamiento'
						value={codigoDireccionamiento}
						onChange={(e) => setCodigoDireccionamiento(e.target.value)}
						onKeyDown={handleKeyDownBuscar}
					/>
				</div>
				<Button variant='contained' onClick={handleBuscar}>
					Buscar
				</Button>
				<Button variant='outlined' disabled={!codigoDireccionamiento.trim()} onClick={handleLimpiarFiltros}>
					Limpiar filtros
				</Button>
			</div>

			<div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
				<Stack padding={2}>
					<TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
						<Table style={{ overflowY: 'visible' }}>
							<TableHead className={`${styleScss['th-custom']}`}>
								<TableRow>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Código direccionamiento</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Tipo direccionamiento</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Subtipo direccionamiento</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha de registro</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Estado</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Acciones</h4></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{orientaciones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((orientacion) => {
									const estaAsociada = asociadas.includes(orientacion.id);
									const procesando = procesandoId === orientacion.id;
									return (
										<TableRow key={orientacion.id}>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{orientacion.codigo_direccionamiento}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{orientacion.tipoDireccionamiento?.label}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{orientacion.subtipoDireccionamiento?.label}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{orientacion.fecha_registro ? dayjs(orientacion.fecha_registro).utc().format('DD/MM/YYYY') : ''}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{orientacion.estado}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												<Stack direction='column' spacing={1} alignItems='center'>
													<Button
														variant='contained'
														size='small'
														disabled={estaAsociada || procesando || readOnly}
														onClick={() => handleAsociar(orientacion.id)}
													>
														Agregar asociación
													</Button>
													<Button
														variant='outlined'
														color='error'
														size='small'
														disabled={!estaAsociada || procesando || readOnly}
														onClick={() => handleDesasociar(orientacion.id)}
													>
														Eliminar asociación
													</Button>
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
							{!loading && buscado && orientaciones.length === 0 && (
								<tfoot>
									<TableRow>
										<TableCell colSpan={6}>
											<p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
												No se encontraron registros
											</p>
										</TableCell>
									</TableRow>
								</tfoot>
							)}
						</Table>
						<TablePagination
							rowsPerPageOptions={[10, 20, 50]}
							component='div'
							style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
							count={orientaciones.length}
							rowsPerPage={rowsPerPage}
							page={page}
							labelRowsPerPage='Filas por página'
							onPageChange={(_event, newPage) => setPage(newPage)}
							onRowsPerPageChange={(event) => {
								setRowsPerPage(parseInt(event.target.value, 10));
								setPage(0);
							}}
						/>
					</TableContainer>
				</Stack>
			</div>

			<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={open} onClose={() => setOpen(false)}>
				<Alert onClose={() => setOpen(false)} variant='filled' severity={alert ? 'success' : 'error'} sx={{ whiteSpace: 'pre-line' }}>
					{alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
					{alertMsg}
				</Alert>
			</Snackbar>
		</div>
	);
};

const AdminEventoAsociacionOrientacionesWrapper = () => {
	return (
		<>
			<PageTitle breadcrumbs={[]}>Asociación de Orientaciones</PageTitle>
			<AsociacionOrientacionesPage />
		</>
	);
};

export { AdminEventoAsociacionOrientacionesWrapper, AsociacionOrientacionesPage };
